import React, { useEffect, useState } from "react";
import {
  Drawer,
  Collapse,
  Spin,
  Divider,
  Col,
  Row,
  Descriptions,
  Typography,
  Tag,
  Card,
} from "antd";
import dayjs from "dayjs";
import { getDietHistoryAPI } from "../../../../../services/nutritionist.services";
import useQuery from "../../../../../hooks/useQuery";

import DietHistoryTable from "./DietHistoryTable";
import useIsMobile from "../../../../../hooks/useIsMobile";

const { Panel } = Collapse;

const DietHistoryDrawer = ({ open, onClose }) => {
  const isMobile = useIsMobile();
  const isActive = useQuery()?.status === "current";
  const [expandedData, setExpandedData] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [dietHistory, setDietHistory] = useState([]);
  const { patient } = useQuery();

  useEffect(() => {
    if (open && patient) {
      fetchDietHistory();
      setExpandedData({});
    }
  }, [open, patient]);

  const fetchDietHistory = async () => {
    try {
      const response = await getDietHistoryAPI(patient);
      setDietHistory(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch diet history:", error);
    }
  };

  const handleExpand = async (id) => {
    if (!expandedData[id]) {
      setLoadingId(id);
      try {
        const response = await getDietHistoryAPI(patient, {
          plan_id: id,
        });
        setExpandedData((prev) => ({
          ...prev,
          [id]: response?.data?.data,
        }));
      } catch (error) {
        console.error("Failed to fetch detail for diet history:", error);
      }
      setLoadingId(null);
    }
  };

  // const filterOnDietType = (data) => {
  //   if ((dietDetails?.dietType ?? dietDetails?.ia?.dietType) === "Oral") {
  //     return Object.fromEntries(
  //       Object.entries(data).filter(([key]) =>
  //         ["proteins", "protein", "calories"].includes(key)
  //       )
  //     );
  //   }
  //   return data;
  // };

  // const calculateDietTotals = (data) => {
  //   // console.log("data", data);
  //   return data?.reduce(
  //     (totals, timeSlot) => {
  //       timeSlot?.items?.forEach((item) => {
  //         totals.proteins += (item.diet__protien || 0) * item.quantity;
  //         totals.calories += (item.diet__calories || 0) * item.quantity;
  //         totals.sodium += (item.diet__sodium || 0) * item.quantity;
  //         totals.potassium += (item.diet__potassium || 0) * item.quantity;
  //         totals.carbohydrate +=
  //           (item.diet__carbohydrates || 0) * item.quantity;
  //       });

  //       return totals;
  //     },
  //     { calories: 0, proteins: 0, carbohydrate: 0, sodium: 0, potassium: 0 }
  //   );
  // };

  const renderDietCollapse = () => (
    <Collapse
      onChange={(key) => {
        const numericKey = Number(key);
        if (numericKey) handleExpand(numericKey);
      }}
      accordion
    >
      {dietHistory.map((entry, index) => (
        <Panel
          key={entry.id}
          header={`${dayjs(entry.created_at).format(
            "DD MMM YYYY, hh:mm A"
          )} - Submitted by ${entry.submitted_by_name}`}
          style={{ backgroundColor: "#ffffff" }}
        >
          {loadingId === entry.id ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "120px",
              }}
            >
              <Spin size="small" />
            </div>
          ) : expandedData[entry.id] ? (
            <>
              <p>
                <strong>Comments:</strong>{" "}
                {expandedData[entry.id]?.comments || "No comments"}
              </p>
              {(() => {
                // const dietTotals = filterOnDietType(
                //   calculateDietTotals([...overallData])
                // );
                // const required =
                //   dietDetails?.nutritional_recommendations ??
                //   dietDetails?.ia?.nutritional_recommendations;
                // if (!required) {
                //   return (
                //     <>Diet Items {mapToCustomTag(dietTotals, "purple")}</>
                //   );
                // }

                // Create combined ratio tags for energy and proteins
                const combinedTags = [];

                // Energy tag (calories)
                if (
                  expandedData[entry.id]?.additional_info?.diet_items
                    .calories !== undefined &&
                  expandedData[entry.id]?.additional_info
                    ?.nutritional_recommendations?.calories !== undefined
                ) {
                  combinedTags.push(
                    <Tag
                      key="energy"
                      color="#FFFAEB"
                      style={{ color: "#B54708", marginBottom: 8 }}
                    >
                      Energy -{" "}
                      {
                        expandedData[entry.id]?.additional_info?.diet_items
                          .calories
                      }
                      /
                      {
                        expandedData[entry.id]?.additional_info
                          ?.nutritional_recommendations?.calories
                      }{" "}
                      kcal
                    </Tag>
                  );
                }

                // Proteins tag
                if (
                  expandedData[entry.id]?.additional_info?.diet_items
                    .proteins !== undefined &&
                  expandedData[entry.id]?.additional_info
                    ?.nutritional_recommendations?.protein !== undefined
                ) {
                  combinedTags.push(
                    <Tag
                      key="proteins"
                      color="color-mix(in srgb, var(--ant-primary-color) 10%, white)"
                      style={{
                        color: "var(--ant-primary-color)",
                        marginBottom: 8,
                      }}
                    >
                      Proteins -{" "}
                      {
                        expandedData[entry.id]?.additional_info?.diet_items
                          .proteins
                      }
                      /
                      {
                        expandedData[entry.id]?.additional_info
                          ?.nutritional_recommendations?.protein
                      }{" "}
                      g
                    </Tag>
                  );
                }

                return <> {combinedTags}</>;
              })()}
              <DietHistoryTable data={expandedData[entry.id]?.diet_data} />
            </>
          ) : (
            <p style={{ fontStyle: "italic", color: "#888" }}>
              Click to load details...
            </p>
          )}

          {index !== dietHistory.length - 1 && <Divider />}
        </Panel>
      ))}
    </Collapse>
  );

  return isActive ? (
    <Drawer
      title="Diet History"
      placement="right"
      width={isMobile ? "100%" : "75%"}
      height={isMobile ? "100%" : undefined}
      onClose={() => {
        onClose();
        setExpandedData({});
      }}
      open={open}
      styles={{
        body: { backgroundColor: "#f5f6f8" },
        wrapper: isMobile
          ? { width: "100%", maxWidth: "100vw" }
          : undefined,
      }}
      destroyOnClose
    >
      {renderDietCollapse()}
    </Drawer>
  ) : (
    <Card title="Diet History">{renderDietCollapse()}</Card>
  );
};

export default DietHistoryDrawer;

// const customSection = (icon, title, mealData) => (
//   <Descriptions
//     title={
//       <Row type="flex" align={"middle"}>
//         <Col span={3}>{icon}</Col>
//         <Col span={4}>
//           <Typography>{title}</Typography>
//         </Col>
//         {/* {mealData &&
//               mapToCustomTag(calculateDietTotals(mealData), "orange")} */}
//       </Row>
//     }
//     bordered
//     labelStyle={{ width: "20%" }}
//   >
//     {mealData?.map((day) => (
//       <Descriptions.Item
//         key={day?.time} // Add key for better rendering
//         label={dayjs(String(day?.time), "HH").format("hh A")}
//         span={3}
//       >
//         {day?.items?.map((item, index) => (
//           <Tag key={index} color="blue" style={{ marginBottom: 8 }}>
//             {item?.diet__name} x {item?.quantity}
//           </Tag>
//         ))}
//         <br />
//         {day?.items[0]?.comments && (
//           <Tag
//             color="purple"
//             style={{
//               maxWidth: "100%",
//               whiteSpace: "normal",
//               wordBreak: "break-word",
//             }}
//           >
//             {day?.items[0]?.comments}
//           </Tag>
//         )}
//       </Descriptions.Item>
//     ))}
//   </Descriptions>
// );
